/**
 * useInactivityTracker Hook - Tracks user activity and triggers inactivity callbacks
 * Monitors user activity (mouse, keyboard, scroll, touch, click events)
 * Triggers warning after 5 minutes of inactivity
 */

import { useEffect, useRef, useCallback } from "react";

const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
const COUNTDOWN_DURATION = 30 * 1000; // 30 seconds in milliseconds

/**
 * Custom hook for tracking user inactivity
 * @param {Function} onInactivity - Callback when inactivity threshold is reached
 * @param {Function} onActivityReset - Callback when activity resets the timer
 * @param {boolean} enabled - Whether the tracker is enabled (default: true)
 * @returns {Object} { startCountdown, cancelCountdown, resetTimer }
 */
export const useInactivityTracker = (
  onInactivity,
  onActivityReset,
  enabled = true
) => {
  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const isWarningShownRef = useRef(false);

  const resetTimer = useCallback(() => {
    if (isWarningShownRef.current) {
      // If warning is shown, reset it
      isWarningShownRef.current = false;
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
      if (onActivityReset) {
        onActivityReset();
      }
    }

    lastActivityRef.current = Date.now();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (enabled) {
      timeoutRef.current = setTimeout(() => {
        isWarningShownRef.current = true;
        if (onInactivity) {
          onInactivity();
        }
      }, INACTIVITY_THRESHOLD);
    }
  }, [onInactivity, onActivityReset, enabled]);

  const handleActivity = useCallback(() => {
    if (!isWarningShownRef.current && enabled) {
      resetTimer();
    }
  }, [resetTimer, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Clean up timers if disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    // Track various user activities
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialize timer
    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [handleActivity, resetTimer, enabled]);

  const startCountdown = useCallback((onCountdownEnd) => {
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
    }
    countdownRef.current = setTimeout(() => {
      if (onCountdownEnd) {
        onCountdownEnd();
      }
    }, COUNTDOWN_DURATION);
  }, []);

  const cancelCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  return {
    startCountdown,
    cancelCountdown,
    resetTimer,
  };
};
